import { PRIMEIRA_ETAPA, ULTIMA_ETAPA } from "./nova-obra.types";
import type {
  DadosObraEtapa1,
  NovaObraEtapa,
  NovaObraEtapaExtra,
  NovaObraState,
} from "./nova-obra.types";
import { isDuracaoVazia, readDuracao, validateEtapaDuracao } from "./nova-obra.duracao";
import {
  limparPorTrocaDeEmbarcacao,
  readTiposObra,
  temConteudoTiposObra,
  validateEtapaTipos,
} from "./nova-obra.tipos";
import {
  isEquipeVazia,
  periodoDaObraNoWizard,
  readEquipe,
  validateEtapaEquipe,
} from "./nova-obra.equipe";
import { validateEtapaObra } from "./nova-obra.validation";

export const DADOS_OBRA_INICIAIS: DadosObraEtapa1 = {
  embarcacaoId: null,
  localPlanejadoId: null,
  manutencaoCorretiva: false,
  statusObraId: null,
  // RN06: sem manutenção corretiva, a obra começa em FEL-0 (visível e desabilitado).
  statusFinanceiro: "FEL_0",
  observacoes: "",
  descricao: "",
};

export function createInitialNovaObraState(): NovaObraState {
  return {
    etapaAtual: PRIMEIRA_ETAPA,
    maiorEtapaAlcancada: PRIMEIRA_ETAPA,
    obra: { ...DADOS_OBRA_INICIAIS },
    etapas: {},
    justificativaCondicoes: "",
    etapasComErroVisivel: [],
  };
}

export type NovaObraAction =
  | { type: "atualizar-obra"; campos: Partial<DadosObraEtapa1> }
  | { type: "alternar-manutencao-corretiva"; ativa: boolean }
  | { type: "trocar-embarcacao"; embarcacaoId: string | null }
  | { type: "salvar-etapa"; etapa: NovaObraEtapaExtra; dados: unknown }
  | { type: "definir-justificativa-condicoes"; texto: string }
  | { type: "ir-para-etapa"; etapa: NovaObraEtapa }
  | { type: "mostrar-erros"; etapa: NovaObraEtapa }
  | { type: "reiniciar" };

/** Erros da etapa; a etapa 5 acrescenta o seu validador na HU própria. */
export function validateEtapa(state: NovaObraState, etapa: NovaObraEtapa): Record<string, string> {
  if (etapa === 1) return validateEtapaObra(state.obra);
  if (etapa === 2) return validateEtapaDuracao(readDuracao(state.etapas.duracao));
  if (etapa === 3) return validateEtapaTipos(readTiposObra(state.etapas.tiposObra));
  if (etapa === 4) {
    return validateEtapaEquipe(
      readEquipe(state.etapas.equipe),
      periodoDaObraNoWizard(state.etapas.duracao),
    );
  }
  return {};
}

/** Há conteúdo nas etapas que dependem da embarcação (Tipos de Obra, RN03)? */
export function hasDadosDependentesDaEmbarcacao(state: NovaObraState): boolean {
  return temConteudoTiposObra(readTiposObra(state.etapas.tiposObra));
}

/** Há alterações em relação ao início do wizard? Base do aviso de saída (Cancelar/X/F5). */
export function isNovaObraDirty(state: NovaObraState): boolean {
  const inicial = DADOS_OBRA_INICIAIS;
  const { obra } = state;

  return (
    obra.embarcacaoId !== inicial.embarcacaoId ||
    obra.localPlanejadoId !== inicial.localPlanejadoId ||
    obra.manutencaoCorretiva !== inicial.manutencaoCorretiva ||
    obra.statusObraId !== inicial.statusObraId ||
    obra.statusFinanceiro !== inicial.statusFinanceiro ||
    obra.observacoes !== inicial.observacoes ||
    obra.descricao !== inicial.descricao ||
    Object.entries(state.etapas).some(([etapa, dados]) => {
      if (dados === undefined) return false;
      if (etapa === "duracao") return !isDuracaoVazia(readDuracao(dados));
      // O CC principal pré-selecionado não é alteração do usuário; só tipos e CCs adicionais.
      if (etapa === "tiposObra") return temConteudoTiposObra(readTiposObra(dados));
      if (etapa === "equipe") return !isEquipeVazia(readEquipe(dados));
      return true;
    })
  );
}

export function novaObraReducer(state: NovaObraState, action: NovaObraAction): NovaObraState {
  switch (action.type) {
    case "atualizar-obra":
      return { ...state, obra: { ...state.obra, ...action.campos } };

    case "alternar-manutencao-corretiva":
      // RN06/RN07: ativar libera o campo sem valor; desativar volta a FEL-0.
      return {
        ...state,
        obra: {
          ...state.obra,
          manutencaoCorretiva: action.ativa,
          statusFinanceiro: action.ativa ? null : "FEL_0",
        },
      };

    case "trocar-embarcacao": {
      // RN03: sai o que depende da embarcação — CCs adicionais (com tipos e escopos) e o CC
      // principal, que volta a ser escolhido. Equipamentos Motor/Thruster/Propulsão: HU 2530.
      const trocou = state.obra.embarcacaoId !== action.embarcacaoId;
      const etapas =
        trocou && state.etapas.tiposObra !== undefined
          ? {
              ...state.etapas,
              tiposObra: limparPorTrocaDeEmbarcacao(readTiposObra(state.etapas.tiposObra)),
            }
          : state.etapas;

      return { ...state, etapas, obra: { ...state.obra, embarcacaoId: action.embarcacaoId } };
    }

    case "salvar-etapa":
      return { ...state, etapas: { ...state.etapas, [action.etapa]: action.dados } };

    case "definir-justificativa-condicoes":
      return { ...state, justificativaCondicoes: action.texto };

    case "ir-para-etapa": {
      const etapa = Math.min(Math.max(action.etapa, PRIMEIRA_ETAPA), ULTIMA_ETAPA) as NovaObraEtapa;
      return {
        ...state,
        etapaAtual: etapa,
        maiorEtapaAlcancada: Math.max(state.maiorEtapaAlcancada, etapa) as NovaObraEtapa,
      };
    }

    case "mostrar-erros":
      return state.etapasComErroVisivel.includes(action.etapa)
        ? state
        : { ...state, etapasComErroVisivel: [...state.etapasComErroVisivel, action.etapa] };

    case "reiniciar":
      return createInitialNovaObraState();
  }
}
